import StaticPage from "./StaticPage";

const content = {
  en: {
    title: "Terms of Service",
    subtitle: "The rules for using FileNesto's online file tools.",
    description: "FileNesto Terms of Service: rules of use, acceptable content, intellectual property, disclaimers, and applicable law (Morocco).",
    lastUpdated: "February 2026",
    sections: [
      {
        h2: "1. Acceptance of terms",
        body: "By accessing or using FileNesto (the \"Service\"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.",
      },
      {
        h2: "2. Description of the service",
        body: "FileNesto provides online file processing utilities, currently focused on PDF tools, with Excel, Word, image and document tools planned. The Service is provided free of charge for personal and commercial use, subject to these Terms.",
      },
      {
        h2: "3. Acceptable use",
        list: [
          "Do not upload files containing illegal, harmful, defamatory, or infringing content.",
          "Do not attempt to disrupt, overload, or reverse-engineer the Service.",
          "Do not use the Service to process files for which you do not have the right to process.",
          "Do not abuse the Service via automated scripts that exceed reasonable use.",
          "Do not upload malware, viruses, or files designed to harm other systems.",
        ],
      },
      {
        h2: "4. Intellectual property",
        body: "All files you upload remain your property. FileNesto does not claim any rights to the content you process. The FileNesto brand, design, code, and content (excluding user files) are the property of FileNesto and protected by applicable intellectual property laws.",
      },
      {
        h2: "5. No account required",
        body: "FileNesto does not require an account to use any tool. We do not collect, store, or maintain user credentials.",
      },
      {
        h2: "6. Service availability",
        body: "The Service is provided \"as is\" and \"as available\". We do not guarantee uninterrupted operation, error-free results, or constant availability. Maintenance, upgrades, or technical issues may cause temporary downtime.",
      },
      {
        h2: "7. Disclaimer of warranties",
        body: "To the fullest extent allowed by law, FileNesto disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the output of any tool will meet your specific requirements.",
      },
      {
        h2: "8. Limitation of liability",
        body: "FileNesto, its operators, and contributors shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from the use of, or inability to use, the Service — including loss of data or files, even if advised of the possibility of such damages.",
      },
      {
        h2: "9. Advertising",
        body: "The Service may display third-party advertising (Google AdSense). FileNesto is not responsible for the content of these ads or for the products and services they promote.",
      },
      {
        h2: "10. Modifications",
        body: "We may update these Terms at any time. Continued use of the Service after any update constitutes acceptance of the updated Terms.",
      },
      {
        h2: "11. Governing law",
        body: "These Terms are governed by and interpreted in accordance with the laws of Morocco. Any dispute arising from the use of the Service shall be submitted to the competent courts of Morocco, unless otherwise required by applicable consumer law in your country of residence.",
      },
      {
        h2: "12. Contact",
        body: "For questions about these Terms, please email contact@filenesto.com.",
      },
    ],
  },
  fr: {
    title: "Conditions d'utilisation",
    subtitle: "Les règles d'utilisation des outils en ligne FileNesto.",
    description: "Conditions d'utilisation FileNesto : règles d'usage, contenus acceptables, propriété intellectuelle, limitations de responsabilité, droit applicable (Maroc).",
    lastUpdated: "Février 2026",
    sections: [
      {
        h2: "1. Acceptation des conditions",
        body: "En accédant à ou en utilisant FileNesto (le « Service »), vous acceptez d'être lié par ces Conditions d'utilisation. Si vous n'acceptez pas, merci de ne pas utiliser le Service.",
      },
      {
        h2: "2. Description du service",
        body: "FileNesto propose des utilitaires en ligne de traitement de fichiers, actuellement centrés sur les outils PDF, avec des outils Excel, Word, image et documents prévus. Le Service est fourni gratuitement pour usage personnel et commercial, sous réserve des présentes Conditions.",
      },
      {
        h2: "3. Usage acceptable",
        list: [
          "Ne pas télécharger de fichiers contenant des contenus illégaux, nuisibles, diffamatoires ou contrefaisants.",
          "Ne pas tenter de perturber, surcharger ou rétro-ingénierer le Service.",
          "Ne pas utiliser le Service pour traiter des fichiers dont vous n'avez pas le droit de traiter.",
          "Ne pas abuser du Service via des scripts automatisés dépassant un usage raisonnable.",
          "Ne pas télécharger de malwares, virus ou fichiers conçus pour endommager d'autres systèmes.",
        ],
      },
      {
        h2: "4. Propriété intellectuelle",
        body: "Tous les fichiers que vous uploadez restent votre propriété. FileNesto ne revendique aucun droit sur le contenu que vous traitez. La marque, le design, le code et le contenu de FileNesto (hors fichiers utilisateurs) sont la propriété de FileNesto et protégés par les lois de propriété intellectuelle applicables.",
      },
      {
        h2: "5. Aucun compte requis",
        body: "FileNesto n'exige pas de compte pour utiliser ses outils. Nous ne collectons, ne stockons ni ne maintenons aucun identifiant utilisateur.",
      },
      {
        h2: "6. Disponibilité du service",
        body: "Le Service est fourni « tel quel » et « selon disponibilité ». Nous ne garantissons pas un fonctionnement ininterrompu, des résultats sans erreur, ni une disponibilité permanente. Maintenance, mises à jour ou problèmes techniques peuvent causer des indisponibilités temporaires.",
      },
      {
        h2: "7. Limitation de garantie",
        body: "Dans toute la mesure permise par la loi, FileNesto exclut toute garantie, expresse ou implicite, notamment de qualité marchande, d'adéquation à un usage particulier et de non-contrefaçon. Nous ne garantissons pas que la sortie d'un outil correspondra à vos exigences spécifiques.",
      },
      {
        h2: "8. Limitation de responsabilité",
        body: "FileNesto, ses opérateurs et contributeurs ne pourront être tenus responsables de tout dommage direct, indirect, accessoire, consécutif ou spécial résultant de l'utilisation ou de l'impossibilité d'utiliser le Service — y compris la perte de données ou de fichiers, même si la possibilité de tels dommages avait été signalée.",
      },
      {
        h2: "9. Publicité",
        body: "Le Service peut afficher des publicités de tiers (Google AdSense). FileNesto n'est pas responsable du contenu de ces publicités ni des produits et services qu'elles promeuvent.",
      },
      {
        h2: "10. Modifications",
        body: "Nous pouvons mettre à jour ces Conditions à tout moment. La poursuite de l'utilisation du Service après mise à jour vaut acceptation des Conditions mises à jour.",
      },
      {
        h2: "11. Droit applicable",
        body: "Les présentes Conditions sont régies par et interprétées conformément aux lois du Maroc. Tout litige résultant de l'utilisation du Service sera soumis aux tribunaux compétents du Maroc, sauf disposition contraire du droit de la consommation applicable dans votre pays de résidence.",
      },
      {
        h2: "12. Contact",
        body: "Pour toute question concernant ces Conditions, écrivez à contact@filenesto.com.",
      },
    ],
  },
};

export default function TermsOfService() {
  return <StaticPage content={content} slug="/terms-of-service" testid="page-terms" />;
}
